<script setup lang="ts">
import { getCarInfo, imageRekognition, searchDataFromText } from '@/services/carInfo'
import AppButton from '@/components/ui/AppButton.vue'
import AppSelect from '@/components/ui/AppSelect.vue'
import AppFileInput from '@/components/ui/AppFileInput.vue'
import Modal from '@/components/ui/Modal.vue'
import { useRouterStore } from '@/stores/router'
import { pinia } from '@/stores'
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRemoteDiagnose } from '@/stores/remote-diagnose'
import { getCustomer } from '@/services/customer'
import { validateVin } from '@/utils/vin'
import { DSG_MODELS, INVALID_DSG_CODES } from '@/utils/dsg-models'
import CarInfoContent from '@/lang/car-info.json'
import { useLang } from '@/composables/use-lang'
import { InvalidDSGModels } from '@/types/remote-diagnose'

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  }
})

const { push, current } = useRouterStore(pinia)
const remoteDiagnoseStore = useRemoteDiagnose(pinia)
const { setParamsOnStore } = remoteDiagnoseStore
const { state } = storeToRefs(remoteDiagnoseStore)

const langContent = useLang(CarInfoContent)

const modal = ref<typeof Modal>()

const loadingImage = ref<boolean>(false)
const nameFound = ref<boolean>(false)
const errorVIN = ref<string>('')
const model_description = ref<string>('')
const dsgModel = ref<string>('')
const invalidDSGCode = ref<boolean>(false)
const exampleImage = ref<HTMLElement | null>(null)
const disabledButton = computed(() => {
  if (
    state.value.client_name &&
    state.value.vin &&
    validateVin(state.value.vin) &&
    !errorVIN.value &&
    state.value.vehicle_registration_number &&
    !loadingImage.value &&
    !invalidDSGCode.value &&
    dsgModel.value
  ) {
    return false
  }
  return true
})

const fetchCarData = async (vin: string) => {
  loadingImage.value = true
  errorVIN.value = ''
  model_description.value = ''

  try {
    const result = await getCarInfo(vin)

    if (result.errors?.length && !INVALID_DSG_CODES.includes(result.dsg_code)) {
      errorVIN.value = langContent.error
      return null
    }
    model_description.value = result.model_description.toUpperCase()
    handleDsgModel(result)
  } catch (e) {
    console.log('e => ', e)
    errorVIN.value = langContent.error
  } finally {
    loadingImage.value = false
  }
}

const handleDsgModel = (data: any) => {
  if (INVALID_DSG_CODES.includes(data.dsg_code)) {
    invalidDSGCode.value = true
    modal.value?.open()
  } else {
    invalidDSGCode.value = false
  }

  if (data.dsg_family) {
    dsgModel.value = data.dsg_family
    return
  }

  if (data.dsg.length) {
    dsgModel.value = data.dsg?.[0]?.split('_')?.[0] ?? ''
    return
  }

  dsgModel.value = ''
}

const onVINChange = (e: Event) => {
  const el = e.target as HTMLInputElement

  if (validateVin(el.value)) {
    errorVIN.value = ''
    fetchCarData(el.value)
  } else if (el.value) {
    errorVIN.value = langContent.invalid_vin
  } else {
    errorVIN.value = ''
  }

  state.value.vin = el.value
}

const onChange = async (e: Event) => {
  const el = e.target as HTMLInputElement

  if (!el.files) return

  loadingImage.value = true
  const imgRekognitionResult = await imageRekognition(el.files[0], props.aws_url)

  if (imgRekognitionResult) {
    state.value.vin = imgRekognitionResult.vin

    await fetchCarData(imgRekognitionResult.vin)
  }

  loadingImage.value = false
}

const handleNext = () => {
  push('address')
}

const handleMouseLeave = () => {
  if (exampleImage.value) exampleImage.value.setAttribute('hidden', 'true')
}

const handleMouseOver = () => {
  if (exampleImage.value) exampleImage.value.removeAttribute('hidden')
}

watch(dsgModel, (newValue) => {
  const dsgUnsure = InvalidDSGModels.DSG_UNSURE.split(' ')[0]
  if (newValue === InvalidDSGModels.DSG_UNSURE) {
    if (!validateVin(state.value.vin)) errorVIN.value = langContent.dsg_model_unsure

    state.value.client_dsg_model = dsgUnsure
    return
  }

  state.value.client_dsg_model = newValue || dsgUnsure
})

onMounted(async () => {
  if (current.params.accepted === '1') {
    setParamsOnStore(current.params)
    const result = await getCustomer(current.params.clientId, props.aws_url)

    if (result.data?.length) {
      const [customer] = result.data
      state.value.client_name = customer.name
      state.value.client_email = customer.custom_email
      state.value.new_client = false
      nameFound.value = true
    }
  } else {
    push('home')
  }
})
</script>

<template>
  <div class="text-center">
    <p class="mb-2.5">{{ langContent.heading }}</p>

    <h2 v-if="nameFound">{{ state.client_name }}</h2>
    <input
      v-else
      class="mb-5 md:mb-0"
      :placeholder="langContent.name_placeholder"
      v-model="state.client_name"
    />

    <form class="relative mt-5 max-w-5xl mx-auto">
      <div class="relative">
        <label class="text-base"
          >{{ langContent.vin_label }}
          <span
            @mouseover="handleMouseOver"
            @mouseleave="handleMouseLeave"
            id="vin-tooltip"
            class="inline-block bg-black rounded-full h-4 w-4 text-center text-white text-xs cursor-pointer"
            >&#63;</span
          >
        </label>

        <div ref="exampleImage" hidden class="border absolute bottom-full right-1/4">
          <img src="@/assets/images/vin_example.jpeg" width="214" height="36" />
        </div>

        <div class="flex justify-center items-center">
          <input
            v-on:blur="onVINChange"
            class="disabled:cursor-not-allowed"
            type="text"
            placeholder="VIN"
            v-model="state.vin"
            :disabled="loadingImage"
            required
          />
          <app-file-input :loadingImage="loadingImage" @onChange="onChange" />
        </div>
      </div>

      <p class="mt-5 text-sweet-brown text-sm" v-if="errorVIN">
        {{ errorVIN
        }}<a class="text-english-vermillion" href="https://wa.me/31638746918">+31 6 38746918</a>
      </p>

      <div class="flex justify-center mt-2.5">
        <img
          v-if="loadingImage"
          class="animate-spin ml-2"
          alt="Loading icon"
          src="@/assets/icons/loading.svg"
          width="50"
          height="50"
        />
      </div>

      <div class="mt-2" v-if="model_description">
        <p class="text-base">
          <strong>{{ langContent.description }}: </strong>{{ model_description }} -
          <strong>DSG:</strong>{{ dsgModel }}
        </p>
      </div>

      <div class="mt-2">
        <label class="relative block text-base">
          {{ langContent.dsg_model_label }}
          <span
            id="dsg-model-tooltip"
            :data-tooltip="langContent.dsg_model_tooltip"
            class="inline-block bg-black rounded-full h-4 w-4 text-center text-white text-xs cursor-pointer"
            >&#63;</span
          >
        </label>
        <app-select v-model="dsgModel" label="DSG Model" :options="DSG_MODELS" />

        <p class="text-xs">
          {{ langContent.dsg_model_unknown }}
          <a class="text-english-vermillion" href="https://wa.me/31638746918">+31 6 38746918</a>
        </p>
      </div>

      <input
        class="mt-5 md:mb-0"
        type="text"
        :placeholder="langContent.plate_placeholder"
        v-model="state.vehicle_registration_number"
        required
      />

      <p v-if="invalidDSGCode" class="mt-5 text-sweet-brown text-sm">
        {{ langContent.modal_text }}
      </p>

      <div v-if="invalidDSGCode" class="flex justify-center mt-5">
        <a
          class="font-bold bg-english-vermillion text-white p-2 rounded hover:bg-sweet-brown"
          :href="langContent.appointment_link"
          target="_blank"
          >{{ langContent.appointment_label }}</a
        >
        <a
          class="font-bold bg-english-vermillion text-white p-2 rounded hover:bg-sweet-brown ml-5"
          :href="langContent.transportation_link"
          target="_blank"
          >{{ langContent.transportation_label }}</a
        >
      </div>
      <div v-else class="flex justify-center mt-5">
        <app-button type="button" @click="handleNext" :disabled="disabledButton">{{
          langContent.submit_button
        }}</app-button>
      </div>
    </form>
  </div>
  <modal ref="modal">
    <section>
      <p class="mb-5">{{ langContent.modal_text }}</p>
      <p class="mb-5">
        {{ langContent.modal_text2 }}
        <a
          class="font-bold text-english-vermillion"
          :href="langContent.appointment_link"
          target="_blank"
          >{{ langContent.appointment_label }}</a
        >
      </p>
      <p>
        {{ langContent.modal_text3 }}
        <a
          class="font-bold text-english-vermillion"
          :href="langContent.transportation_link"
          target="_blank"
          >{{ langContent.transportation_label }}</a
        >
      </p>
    </section>
  </modal>
</template>

<style>
#vin-tooltip::after {
  content: '';
  display: none;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid black;
  top: 0;
  position: absolute;
}

#vin-tooltip:hover::after {
  display: block;
}

#dsg-model-tooltip::after {
  content: attr(data-tooltip);
  display: none;
  background-color: black;
  color: white;
  bottom: 80%;
  position: absolute;
}

#dsg-model-tooltip:hover::after {
  display: block;
}

@media screen and (max-width: 600px) {
  #dsg-model-tooltip::after {
    width: 100%;
    left: 0;
  }
}
</style>
